import * as React from "react";
import { observer } from "mobx-react-lite";
import { GmMigrationAutoAllocationState } from "../../GmMigrationService";
import { Box, Button, Card, FormHelperText, Grid, List, ListItem, ListItemIcon, ListItemText, SvgIcon, TextField, Typography } from "@mui/material";
import { useEffectOnce } from "react-use";
import { AutoAlloc } from "../../../../_proto/galaxymigratepb/galaxy_migrate_autoalloc_pb";
import { DiskIcon } from "../../../../common/CommonIcons";
import { formatKnownDataType, KnownDataType } from "../../../../common/utils/formatter";
import { BlockDeviceInfo } from "../../../../_proto/galaxymigratepb/galaxy_migrate_storage_pb";
import { useMemo, useState } from "react";
import produce from "immer";

// ======================
// PureCBSAllocateVolumesStep
// ======================
interface VendorAllocateVolumesStepProps {
    allocateFunc: () => Promise<void>;
    state: GmMigrationAutoAllocationState;
}

export const PureCBSAllocateVolumesStep: React.FC<VendorAllocateVolumesStepProps> = observer((p) => {
    const state = p.state;
    const allocateNow = p.allocateFunc;

    useEffectOnce(() => {
        for (let device of state.sourceDevices) {
            device.autoAllocParams.setPurecbs(new AutoAlloc.VolumeParams.PureCBS());
        }
    });

    const getInitialDeviceAssignments = () => {
        const assignments: { [key: string]: number } = {};
        for (let device of state.sourceDevices) {
            const blockDevice = device.source.getBlockDevice();
            assignments[blockDevice.getDeviceName()] = blockDevice.getCapacity() / Math.pow(1024, 3);
        }
        return assignments;
    };

    const [deviceConfigs, setDeviceConfigs] = useState<{ [key: string]: number }>(getInitialDeviceAssignments());

    const setDeviceCapacity = (deviceName: string, capacity: number) => {
        setDeviceConfigs(
            produce((draft) => {
                if (capacity > 0) {
                    draft[deviceName] = capacity;
                } else {
                    draft[deviceName] = null;
                }
            })
        );
        const sourceDeviceInAutoAllocState = state.sourceDevices.find((s) => s.source.getBlockDevice().getDeviceName() === deviceName);
        sourceDeviceInAutoAllocState.autoAllocVolumeCapacity = capacity;
    };

    const getHasError = () => {
        if (state.allowSmallerDestinations) {
            return false;
        }
        for (let device in deviceConfigs) {
            const blockDevice = state.sourceDevices.find((d) => d.source.getBlockDevice().getDeviceName() === device);
            if (deviceConfigs[device] < blockDevice?.source.getBlockDevice().getCapacity() / Math.pow(1024, 3)) {
                return true;
            }
        }
        return false;
    };

    return (
        <>
            <Typography color={"textSecondary"}>
                {`Destination Volumes will be allocated from the connected storage to match the following source volumes`}
            </Typography>
            <br />
            <List>
                {state.sourceDevices.map((device) => {
                    const blockDevice = device.source.getBlockDevice();
                    return (
                        <PureCbsDiskCard
                            state={state}
                            blockDevice={blockDevice}
                            setDeviceCapacity={setDeviceCapacity}
                            currentDeviceCapacity={deviceConfigs[blockDevice.getDeviceName()]}
                        />
                    );
                })}
            </List>
            <Box pt={2} pb={2}>
                <Button color={"primary"} variant={"contained"} onClick={allocateNow} disabled={getHasError()}>
                    {`Allocate Volumes (${state.sourceDevices.length})`}
                </Button>
            </Box>
        </>
    );
});

// ======================
// PureCbsDiskCard
// ======================

interface PureCbsDiskCardProps {
    state: GmMigrationAutoAllocationState;
    blockDevice: BlockDeviceInfo;
    currentDeviceCapacity: number;
    setDeviceCapacity: (deviceName: string, capacity: number) => void;
}

const PureCbsDiskCard: React.FC<PureCbsDiskCardProps> = observer((p) => {
    const { state, blockDevice, currentDeviceCapacity, setDeviceCapacity } = p;

    const defaultHelperText = "Set a custom disk size.";

    const [helperText, setHelperText] = useState(defaultHelperText);

    const getError = () => {
        if (state.allowSmallerDestinations) {
            return false;
        } else {
            return currentDeviceCapacity < blockDevice.getCapacity() / Math.pow(1024, 3);
        }
    };

    return (
        <Card sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <ListItem key={blockDevice.getDeviceName()}>
                        <ListItemIcon>
                            <DiskIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary={`${blockDevice.getDeviceName()} (${blockDevice.getDeviceType()})`}
                            secondary={formatKnownDataType(blockDevice.getCapacity(), KnownDataType.CAPACITY)}
                        />
                    </ListItem>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box p={2}>
                        <TextField
                            fullWidth
                            label={"Disk Size"}
                            variant={"filled"}
                            error={getError()}
                            value={currentDeviceCapacity}
                            type={"number"}
                            onChange={(e) => {
                                setDeviceCapacity(blockDevice.getDeviceName(), Number(e.target.value));
                                if (!state.allowSmallerDestinations && Number(e.target.value) < blockDevice.getCapacity() / Math.pow(1024, 3)) {
                                    setHelperText("Must be greater than or equal to source volume size.");
                                } else {
                                    setHelperText(defaultHelperText);
                                }
                            }}
                            helperText={helperText}
                            InputProps={{ endAdornment: <Typography>{`GiB`}</Typography> }}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
});
